/**
 * Client helpers for the entities/memberships collections. Subtree browsing
 * (a director listing every squad under their team) intentionally goes
 * through the backend API, not a direct Firestore collection query -- rules
 * can't express the dynamic "IN visible roster" predicate the backend's
 * visibility resolver computes (see backend/app/services/visibility.py).
 */
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/js/firebase'
import { getBackendUrl } from '@/services/aeroApi'
import { authenticatedFetch } from '@/services/backendAuth'
import type { AthleteSessionsPage, EntityDoc, EntityRole, EntityType, HierarchyEntity, MembershipDoc, Sport } from '@/types/firestore'

export async function getEntity(entityId: string): Promise<EntityDoc | null> {
  const snap = await getDoc(doc(db, 'entities', entityId))
  return snap.exists() ? (snap.data() as EntityDoc) : null
}

/** Direct memberships of this entity only (not descendants) -- for roster UI. */
export async function listMembers(entityId: string): Promise<MembershipDoc[]> {
  const snap = await getDocs(collection(db, 'entities', entityId, 'memberships'))
  return snap.docs.map((d) => d.data() as MembershipDoc)
}

/** Every entity the caller is an active member of, via the memberships collection group. */
export async function listMyEntityMemberships(uid: string): Promise<MembershipDoc[]> {
  const cgQuery = query(
    collectionGroup(db, 'memberships'),
    where('userId', '==', uid),
    where('status', '==', 'active'),
  )
  const snap = await getDocs(cgQuery)
  return snap.docs.map((d) => d.data() as MembershipDoc)
}

/** Admin-only: every entity in the system, for the hierarchy tree/pickers. Rules grant admins unrestricted list. */
export async function listAllEntities(): Promise<EntityDoc[]> {
  const snap = await getDocs(collection(db, 'entities'))
  return snap.docs.map((d) => d.data() as EntityDoc)
}

/** Admin-only: every membership in the system (which entity/role each user has), for the User Management table. */
export async function listAllMemberships(): Promise<MembershipDoc[]> {
  const snap = await getDocs(collectionGroup(db, 'memberships'))
  return snap.docs.map((d) => d.data() as MembershipDoc)
}

/** Admin-only: create a root or nested entity. Backend computes ancestorIds/depth. */
export async function createEntity(
  name: string,
  type: EntityType,
  parentId: string | null
): Promise<{ entity_id: string }> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/entities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, type, parent_id: parentId })
  })
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to create entity.')
  }
  return res.json()
}

/** Admin-only: assign a user to an entity with a role. */
export async function addMembership(entityId: string, userId: string, role: EntityRole): Promise<void> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/entities/${entityId}/memberships`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, role })
  })
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to add membership.')
  }
}

export interface MyEntitySummary {
  entity_id: string
  name: string
  type: EntityType
  parent_id: string | null
  depth: number
}

/** Non-admin: entities the caller's own visibility context covers (self + subtree for owner/director/coach/analyst). */
export async function listMyManagedEntities(): Promise<MyEntitySummary[]> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/my-entities`)
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to load entities.')
  }
  return res.json()
}

export interface UnassignedUser {
  id: string
  fullname: string
  email: string
  is_managed: boolean
}

/**
 * Users not currently an active member of any entity -- the picker for the
 * My Team "Assign User" dialog, so a manager can't double-assign someone
 * already on another Team/Squad's roster. Goes through the backend for the
 * same cross-entity-read reason as fetchMyHierarchy (firestore.rules denies
 * non-admins a `users` collection scan cross-referenced against every
 * entity's memberships).
 */
export async function fetchUnassignedUsers(): Promise<UnassignedUser[]> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/unassigned-users`)
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to load unassigned users.')
  }
  return res.json()
}

/**
 * A Manager's full hierarchy in one call: their entity subtree plus, per
 * entity, its managers/athletes and rollup stats (athlete count, session
 * count, last ride). Backs the /my-team landing view -- see
 * backend/app/api/v1/entities.py's my_hierarchy for why this can't be
 * assembled from direct Firestore reads client-side (cross-entity
 * membership/session queries are denied by firestore.rules for non-admins).
 */
export async function fetchMyHierarchy(): Promise<HierarchyEntity[]> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/my-hierarchy`)
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to load hierarchy.')
  }
  return res.json()
}

/** One athlete's sessions, for the /my-team drill-down -- see fetchMyHierarchy's doc comment for why this goes through the backend. */
export interface FetchAthleteSessionsOptions {
  pageSize?: number
  cursor?: string | null
  sortDir?: 'asc' | 'desc'
  includeStats?: boolean
}

/**
 * Paginated + field-projected on the backend (see athlete_sessions in
 * insights.py) -- only requests includeStats on the first page of a session
 * so page turns stay cheap (no repeated count() aggregations).
 */
export async function fetchAthleteSessions(athleteId: string, options: FetchAthleteSessionsOptions = {}): Promise<AthleteSessionsPage> {
  const params = new URLSearchParams()
  params.set('pageSize', String(options.pageSize ?? 50))
  params.set('sortDir', options.sortDir ?? 'desc')
  params.set('includeStats', String(options.includeStats ?? false))
  if (options.cursor) params.set('cursor', options.cursor)

  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/athletes/${athleteId}/sessions?${params.toString()}`)
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to load sessions.')
  }
  return res.json()
}

export interface AthleteProfileUpdate {
  fullname?: string
  sport?: Sport[]
  totalMass?: number
  ftp?: number
  thresholdHr?: number
  restingHr?: number
  maxHr?: number
  thresholdRunPaceSecPerKm?: number
  criticalSwimPaceSecPer100m?: number
}

/**
 * Manager editing a roster athlete's own profile (name/sport/weight) --
 * firestore.rules only allows a user to write their own doc (or a manager
 * their own managed/headless athlete), so a manager editing a real athlete
 * they don't own goes through the backend, same reasoning as fetchAthleteSessions.
 * A self-edit should use a direct Firestore updateDoc instead -- rules already
 * permit it and it avoids the round trip.
 */
export async function updateAthleteProfile(userId: string, payload: AthleteProfileUpdate): Promise<void> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/users/${userId}/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to update profile.')
  }
}

/**
 * What a manager of an entity will be *called*.
 *
 * There is no stored director/coach value -- only 'manager' and 'athlete'.
 * Managing an entity that has children makes you a Director (you oversee
 * squads that have their own managers); managing a leaf makes you a Coach (you
 * run that one roster directly).
 *
 * Takes the same single input as label_for_manager in
 * backend/app/services/entities.py -- whether the managed entity has children
 * -- rather than special-casing "is it a squad". Those are equivalent while the
 * tree is only one level deep, but only this phrasing stays correct if it ever
 * gets deeper, and the two implementations have to agree or a user is called
 * one thing in the UI and another by the API.
 *
 * Shared so the assign and create dialogs cannot drift, and so the rule is
 * testable without mounting a page.
 */
export function managerLabel(hasChildEntities: boolean): 'Director' | 'Coach' {
  return hasChildEntities ? 'Director' : 'Coach'
}

/** The two membership roles, with the manager option labelled for this entity. */
export function entityRoleOptions(hasChildEntities: boolean) {
  return [
    { title: `Manager (shown as ${managerLabel(hasChildEntities)})`, value: 'manager' as EntityRole },
    { title: 'Athlete', value: 'athlete' as EntityRole }
  ]
}

/** Every user doc is readable by any authenticated caller (firestore.rules), so this exact-match query runs client-side. */
export async function isEmailTaken(email: string): Promise<boolean> {
  const snap = await getDocs(query(collection(db, 'users'), where('email', '==', email), limit(1)))
  return !snap.empty
}

export interface CreateManagedUserOptions {
  sport?: Sport[]
  totalMass?: number
}

/**
 * Creates a managed (headless) profile -- a synthetic "managed_<uuid>" id
 * with no Firebase Auth account, same convention as iOS's
 * createManagedAthlete(fullname:). Does NOT assign the new user onto any
 * entity -- callers (My Team's "Add New Athlete", Org Hierarchy's "Add New
 * Manager") follow this with their own addMembership call, since the role
 * ('athlete' vs 'manager') and target entity differ per caller.
 *
 * firestore.rules permits this create for ANY authenticated caller when
 * isManaged == true && managedBy == the caller's own uid -- not manager-only
 * -- so this is equally valid called by an admin.
 */
export async function createManagedUser(
  fullname: string,
  email: string,
  managedByUid: string,
  opts: CreateManagedUserOptions = {}
): Promise<string> {
  const managedId = `managed_${crypto.randomUUID()}`
  await setDoc(doc(db, 'users', managedId), {
    id: managedId,
    fullname: fullname.trim(),
    email,
    role: 'user',
    isFree: true,
    isManaged: true,
    managedBy: managedByUid,
    ...(opts.sport && opts.sport.length > 0 ? { sport: opts.sport } : {}),
    ...(opts.totalMass != null && Number.isFinite(opts.totalMass) ? { defaultAnalysisParams: { totalMass: opts.totalMass } } : {})
  })
  return managedId
}
